import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiLogOut, FiLock, FiKey } from 'react-icons/fi';
import { useAppDispatch, useAppSelector } from '../../store';
import { setBank, setPso, setUser } from '../../store/slices';
import { signOutUser } from '../../screens/login/service/auth';
import Icon from '../Icon';
import ChangePasswordModal from '../ChangePasswordModal';
import ChangeUserPasswordModal from '../ChangeUserPasswordModal';
import LogoutConfirmationModal from '../LogoutConfirmationModal';

/** Prefer A–Z from first / last name word; else first two letters; else graphemes. */
function initialsFromName(label: string): string {
    const trimmed = label.trim();
    if (!trimmed) return '?';

    const firstLatinIn = (s: string) => s.match(/[a-zA-Z]/)?.[0] ?? '';

    const words = trimmed.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
        const a = firstLatinIn(words[0]);
        const b = firstLatinIn(words[words.length - 1]);
        if (a && b) return `${a}${b}`.toUpperCase();
    }

    const allLatin = trimmed.match(/[a-zA-Z]/g);
    if (allLatin && allLatin.length >= 2) {
        return `${allLatin[0]}${allLatin[1]}`.toUpperCase();
    }
    if (allLatin && allLatin.length === 1) {
        return allLatin[0].toUpperCase();
    }

    if (words.length >= 2) {
        const a = words[0].charAt(0);
        const b = words[words.length - 1].charAt(0);
        const pair = `${a}${b}`.toUpperCase();
        if (pair) return pair;
    }

    if (words.length === 1) {
        const w = words[0];
        if (w.length >= 2) return w.slice(0, 2).toUpperCase();
        if (w.length === 1) return w.charAt(0).toUpperCase();
    }
    return '?';
}

export default function UserSettingsDropdown() {
    const [isOpen, setIsOpen] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showUserPasswordModal, setShowUserPasswordModal] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [avatarBroken, setAvatarBroken] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isBankUser = sessionStorage.getItem('role') === 'bank';
    const pso = useAppSelector((s) => s.user.pso);
    const bank = useAppSelector((s) => s.user.bank);
    const currentUser = useAppSelector((s) => s.user.currentUser);

    const displayName =
        pso?.name?.trim() ||
        bank?.companyName?.trim() ||
        currentUser?.displayName?.trim() ||
        currentUser?.email?.split('@')[0]?.trim() ||
        'Account';

    const subtitle = pso?.designation?.trim()
        ? pso.designation.replace(/_/g, ' ')
        : bank
          ? 'Bank portal'
          : currentUser?.email
            ? currentUser.email
            : '';

    /** User profile photo only — bank logo is shown separately in the dashboard header. */
    const userPhotoSrc = currentUser?.photoURL?.trim() || '';

    useEffect(() => {
        setAvatarBroken(false);
    }, [userPhotoSrc]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleLogout = async () => {
        try {
            await signOutUser();
        } finally {
            dispatch(setPso(null));
            dispatch(setBank(null));
            dispatch(setUser(null));
            sessionStorage.removeItem('role');
            sessionStorage.removeItem('psoDesignation');
            sessionStorage.removeItem('toast');
            navigate('/login');
        }
    };

    const handleChangePassword = () => {
        setIsOpen(false);
        setShowPasswordModal(true);
    };

    const handleLogoutClick = () => {
        setIsOpen(false);
        setShowLogoutModal(true);
    };

    const handleChangeUserPassword = () => {
        setIsOpen(false);
        setShowUserPasswordModal(true);
    };

    return (
        <>
            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-expanded={isOpen}
                    aria-haspopup="menu"
                    className="flex max-w-[min(100vw-8rem,20rem)] items-center gap-3 rounded-xl border border-gray-200/80 bg-white py-1.5 pl-1.5 pr-2.5 text-left shadow-sm transition hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900/60 dark:hover:border-gray-500 dark:hover:bg-gray-800/80"
                >
                    {userPhotoSrc && !avatarBroken ? (
                        <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg ring-2 ring-gray-100 dark:ring-gray-700">
                            <img
                                src={userPhotoSrc}
                                alt=""
                                width={40}
                                height={40}
                                className="h-full w-full object-cover object-center"
                                referrerPolicy="no-referrer"
                                decoding="async"
                                onError={() => setAvatarBroken(true)}
                            />
                        </div>
                    ) : (
                        <div
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-slate-600 via-slate-700 to-slate-900 text-sm font-semibold uppercase tracking-wide text-white shadow-inner ring-2 ring-white/10 dark:from-slate-500 dark:via-slate-600 dark:to-slate-800"
                            aria-hidden
                        >
                            {initialsFromName(displayName)}
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{displayName}</div>
                        {subtitle ? (
                            <div className="truncate text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>
                        ) : null}
                    </div>
                    <Icon
                        icon={FiChevronDown}
                        className={`h-4 w-4 shrink-0 text-gray-400 transition-transform dark:text-gray-500 ${isOpen ? 'rotate-180' : ''}`}
                    />
                </button>

                {isOpen && (
                    <div
                        className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-800 dark:ring-white/10"
                        role="menu"
                    >
                        <div className="py-1">
                            <button
                                onClick={handleChangePassword}
                                className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                <Icon icon={FiLock} className="h-4 w-4" />
                                Change Password
                            </button>
                            {isBankUser && (
                                <button
                                    onClick={handleChangeUserPassword}
                                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                                >
                                    <Icon icon={FiKey} className="h-4 w-4" />
                                    Change User Password
                                </button>
                            )}
                            <button
                                onClick={handleLogoutClick}
                                className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                            >
                                <Icon icon={FiLogOut} className="h-4 w-4" />
                                Logout
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <ChangePasswordModal
                isOpen={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
            />

            <ChangeUserPasswordModal
                isOpen={showUserPasswordModal}
                onClose={() => setShowUserPasswordModal(false)}
            />

            <LogoutConfirmationModal
                isOpen={showLogoutModal}
                onConfirm={() => {
                    setShowLogoutModal(false);
                    handleLogout();
                }}
                onCancel={() => setShowLogoutModal(false)}
            />
        </>
    );
}
